import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
    X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight,
    Maximize2, Minimize2, RotateCw, Loader2, GripHorizontal
} from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface StudentPdfPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    pdfUrl: string;
    studentName?: string;
}

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;

const MIN_WIDTH = 280;
const MIN_HEIGHT = 300;

// Helper to get clientX/Y from mouse or touch event
const getPointer = (e: MouseEvent | TouchEvent) => {
    if ('touches' in e) {
        const t = e.touches[0] || e.changedTouches[0];
        return { x: t.clientX, y: t.clientY };
    }
    return { x: e.clientX, y: e.clientY };
};

const isMobile = () => window.innerWidth < 768;

const StudentPdfPreviewModal: React.FC<StudentPdfPreviewModalProps> = ({
    isOpen,
    onClose,
    pdfUrl,
    studentName = 'Student',
}) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [userZoom, setUserZoom] = useState<number>(1.0);
    const [isMaximized, setIsMaximized] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Container dimensions for auto-fit
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // PDF intrinsic page dimensions
    const [pdfPageSize, setPdfPageSize] = useState({ width: 595, height: 842 });

    // Drag state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
    const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0, posX: 0, posY: 0, dir: '' });

    // Pinch-to-zoom state
    const pinchStart = useRef({ dist: 0, zoom: 1 });

    // Save pre-maximize state
    const preMaximize = useRef({ x: 0, y: 0, w: 0, h: 0 });

    // Observe container size changes for auto-fit
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                });
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [isOpen]);

    // Compute the auto-fit scale
    const computedScale = (() => {
        if (containerSize.width === 0 || containerSize.height === 0) return 1;
        const padding = 16;
        const availW = containerSize.width - padding * 2;
        const availH = containerSize.height - padding * 2;

        const isRotated = rotation === 90 || rotation === 270;
        const pageW = isRotated ? pdfPageSize.height : pdfPageSize.width;
        const pageH = isRotated ? pdfPageSize.width : pdfPageSize.height;

        const fitScale = Math.min(availW / pageW, availH / pageH);
        return fitScale * userZoom;
    })();

    // Initialize position/size centered - fullscreen on mobile
    useEffect(() => {
        if (isOpen) {
            if (isMobile()) {
                setSize({ width: window.innerWidth, height: window.innerHeight });
                setPosition({ x: 0, y: 0 });
                setIsMaximized(true);
            } else {
                const w = Math.min(window.innerWidth * 0.85, 1200);
                const h = window.innerHeight * 0.88;
                setSize({ width: w, height: h });
                setPosition({
                    x: (window.innerWidth - w) / 2,
                    y: (window.innerHeight - h) / 2,
                });
                setIsMaximized(false);
            }
            setCurrentPage(1);
            setUserZoom(1.0);
            setRotation(0);
            setLoading(true);
            setError(null);
        }
    }, [isOpen]);

    // --- DRAG LOGIC (mouse + touch) ---
    const startDrag = (clientX: number, clientY: number) => {
        if (isMaximized) return;
        setIsDragging(true);
        dragStart.current = { x: clientX, y: clientY, posX: position.x, posY: position.y };
    };

    const handleDragMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        startDrag(e.clientX, e.clientY);
    };

    const handleDragTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length !== 1) return;
        startDrag(e.touches[0].clientX, e.touches[0].clientY);
    };

    useEffect(() => {
        if (!isDragging) return;
        const handleMove = (e: MouseEvent | TouchEvent) => {
            const p = getPointer(e);
            setPosition({
                x: dragStart.current.posX + (p.x - dragStart.current.x),
                y: dragStart.current.posY + (p.y - dragStart.current.y),
            });
        };
        const handleUp = () => setIsDragging(false);

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleUp);
        window.addEventListener('touchcancel', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
            window.removeEventListener('touchcancel', handleUp);
        };
    }, [isDragging]);

    // --- RESIZE LOGIC (mouse + touch) ---
    const startResize = (clientX: number, clientY: number, dir: string) => {
        if (isMaximized) return;
        setIsResizing(true);
        resizeStart.current = {
            x: clientX, y: clientY,
            w: size.width, h: size.height,
            posX: position.x, posY: position.y,
            dir,
        };
    };

    const handleResizeMouseDown = (e: React.MouseEvent, dir: string) => {
        e.preventDefault();
        e.stopPropagation();
        startResize(e.clientX, e.clientY, dir);
    };

    const handleResizeTouchStart = (e: React.TouchEvent, dir: string) => {
        if (e.touches.length !== 1) return;
        e.stopPropagation();
        startResize(e.touches[0].clientX, e.touches[0].clientY, dir);
    };

    useEffect(() => {
        if (!isResizing) return;
        const handleMove = (e: MouseEvent | TouchEvent) => {
            const p = getPointer(e);
            const { x, y, w, h, posX, posY, dir } = resizeStart.current;
            const dx = p.x - x;
            const dy = p.y - y;

            let newW = w, newH = h, newX = posX, newY = posY;

            if (dir.includes('r')) newW = Math.max(MIN_WIDTH, w + dx);
            if (dir.includes('b')) newH = Math.max(MIN_HEIGHT, h + dy);
            if (dir.includes('l')) {
                newW = Math.max(MIN_WIDTH, w - dx);
                if (newW > MIN_WIDTH) newX = posX + dx;
            }
            if (dir.includes('t')) {
                newH = Math.max(MIN_HEIGHT, h - dy);
                if (newH > MIN_HEIGHT) newY = posY + dy;
            }

            setSize({ width: newW, height: newH });
            setPosition({ x: newX, y: newY });
        };
        const handleUp = () => setIsResizing(false);

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleUp);
        window.addEventListener('touchcancel', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
            window.removeEventListener('touchcancel', handleUp);
        };
    }, [isResizing]);

    // --- PINCH TO ZOOM on PDF container ---
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !isOpen) return;

        const getTouchDist = (e: TouchEvent) => {
            const [a, b] = [e.touches[0], e.touches[1]];
            return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                pinchStart.current = { dist: getTouchDist(e), zoom: userZoom };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const newDist = getTouchDist(e);
                const ratio = newDist / pinchStart.current.dist;
                const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, pinchStart.current.zoom * ratio));
                setUserZoom(newZoom);
            }
        };

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
        };
    }, [isOpen, userZoom]);

    // Keyboard shortcuts
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    setCurrentPage(p => Math.max(1, p - 1));
                    break;
                case 'ArrowRight':
                    setCurrentPage(p => Math.min(numPages, p + 1));
                    break;
                case '+':
                case '=':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        setUserZoom(s => Math.min(MAX_ZOOM, s + ZOOM_STEP));
                    }
                    break;
                case '-':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        setUserZoom(s => Math.max(MIN_ZOOM, s - ZOOM_STEP));
                    }
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, numPages, onClose]);

    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(null);
    }, []);

    const onDocumentLoadError = useCallback((err: Error) => {
        setLoading(false);
        setError('Failed to load PDF. Please try again.');
        console.error('PDF load error:', err);
    }, []);

    const onPageLoadSuccess = useCallback((page: { originalWidth: number; originalHeight: number }) => {
        setPdfPageSize({ width: page.originalWidth, height: page.originalHeight });
    }, []);

    const handleZoomIn = () => setUserZoom(s => Math.min(MAX_ZOOM, s + ZOOM_STEP));
    const handleZoomOut = () => setUserZoom(s => Math.max(MIN_ZOOM, s - ZOOM_STEP));
    const handleResetZoom = () => setUserZoom(1.0);
    const handleRotate = () => setRotation(r => (r + 90) % 360);
    const handlePrevPage = () => setCurrentPage(p => Math.max(1, p - 1));
    const handleNextPage = () => setCurrentPage(p => Math.min(numPages, p + 1));

    const toggleMaximize = () => {
        if (isMaximized) {
            if (isMobile()) {
                // On mobile, "restore" means 90% of screen
                const w = window.innerWidth * 0.92;
                const h = window.innerHeight * 0.7;
                setPosition({ x: (window.innerWidth - w) / 2, y: (window.innerHeight - h) / 2 });
                setSize({ width: w, height: h });
            } else {
                setPosition({ x: preMaximize.current.x, y: preMaximize.current.y });
                setSize({ width: preMaximize.current.w, height: preMaximize.current.h });
            }
            setIsMaximized(false);
        } else {
            preMaximize.current = { x: position.x, y: position.y, w: size.width, h: size.height };
            setPosition({ x: 0, y: 0 });
            setSize({ width: window.innerWidth, height: window.innerHeight });
            setIsMaximized(true);
        }
    };

    const handleHeaderDoubleClick = () => toggleMaximize();

    if (!isOpen) return null;

    const resizeHandleBase = "absolute z-20";
    // Bigger touch targets on mobile
    const handleSize = isMobile() ? { edge: 'h-3', edgeW: 'w-3', corner: 'w-6 h-6' } : { edge: 'h-1.5', edgeW: 'w-1.5', corner: 'w-3 h-3' };

    const edgeHandles = [
        { dir: 't', className: `${resizeHandleBase} top-0 left-4 right-4 ${handleSize.edge} cursor-n-resize` },
        { dir: 'b', className: `${resizeHandleBase} bottom-0 left-4 right-4 ${handleSize.edge} cursor-s-resize` },
        { dir: 'l', className: `${resizeHandleBase} left-0 top-4 bottom-4 ${handleSize.edgeW} cursor-w-resize` },
        { dir: 'r', className: `${resizeHandleBase} right-0 top-4 bottom-4 ${handleSize.edgeW} cursor-e-resize` },
    ];
    const cornerHandles = [
        { dir: 'tl', className: `${resizeHandleBase} top-0 left-0 ${handleSize.corner} cursor-nw-resize` },
        { dir: 'tr', className: `${resizeHandleBase} top-0 right-0 ${handleSize.corner} cursor-ne-resize` },
        { dir: 'bl', className: `${resizeHandleBase} bottom-0 left-0 ${handleSize.corner} cursor-sw-resize` },
        { dir: 'br', className: `${resizeHandleBase} bottom-0 right-0 ${handleSize.corner} cursor-se-resize bg-gray-300/40 rounded-tl-md` },
    ];

    const displayZoomPercent = Math.round(userZoom * 100);

    return (
        <div className="fixed inset-0 z-[100] pointer-events-none">
            <div
                ref={modalRef}
                className={`absolute bg-white flex flex-col overflow-hidden shadow-2xl border border-gray-300 pointer-events-auto ${isMaximized ? 'rounded-none' : 'rounded-xl'}`}
                style={{
                    left: position.x,
                    top: position.y,
                    width: size.width,
                    height: size.height,
                    userSelect: (isDragging || isResizing) ? 'none' : 'auto',
                }}
            >
                {/* Resize Handles */}
                {!isMaximized && (
                    <>
                        {edgeHandles.map(h => (
                            <div key={h.dir} className={h.className}
                                onMouseDown={(e) => handleResizeMouseDown(e, h.dir)}
                                onTouchStart={(e) => handleResizeTouchStart(e, h.dir)} />
                        ))}
                        {cornerHandles.map(h => (
                            <div key={h.dir} className={h.className}
                                onMouseDown={(e) => handleResizeMouseDown(e, h.dir)}
                                onTouchStart={(e) => handleResizeTouchStart(e, h.dir)} />
                        ))}
                    </>
                )}

                {/* Draggable Header */}
                <div
                    className={`px-3 py-2 md:px-4 md:py-2.5 border-b border-gray-200 flex items-center justify-between bg-gray-50 shrink-0 select-none ${isMaximized ? '' : 'cursor-grab active:cursor-grabbing'}`}
                    onMouseDown={handleDragMouseDown}
                    onTouchStart={handleDragTouchStart}
                    onDoubleClick={handleHeaderDoubleClick}
                >
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                        <GripHorizontal className="w-4 h-4 text-gray-300 shrink-0" />
                        <h3 className="text-xs md:text-sm font-bold text-gray-900 truncate">
                            {studentName}'s Answer Sheet
                        </h3>
                        {numPages > 0 && (
                            <span className="text-[10px] md:text-xs text-gray-500 bg-gray-200 px-1.5 md:px-2 py-0.5 rounded-full shrink-0">
                                {numPages} pg
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-0.5 shrink-0">
                        <button
                            onClick={(e) => { e.stopPropagation(); toggleMaximize(); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg transition-colors"
                            title={isMaximized ? 'Restore' : 'Maximize'}
                        >
                            {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            className="p-1.5 md:p-2 hover:bg-red-100 hover:text-red-600 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="px-2 md:px-4 py-1.5 md:py-2 border-b border-gray-100 flex items-center justify-center gap-1 md:gap-2 bg-white shrink-0 flex-wrap">
                    {/* Page Navigation */}
                    <div className="flex items-center gap-0.5 md:gap-1 bg-gray-100 rounded-lg px-1 py-0.5 md:py-1">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage <= 1}
                            className="p-1 md:p-1.5 hover:bg-white rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-[10px] md:text-xs font-semibold text-gray-700 px-1 md:px-2 min-w-[60px] md:min-w-[80px] text-center">
                            {currentPage} / {numPages || '...'}
                        </span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage >= numPages}
                            className="p-1 md:p-1.5 hover:bg-white rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="w-px h-5 md:h-6 bg-gray-200" />

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-0.5 md:gap-1 bg-gray-100 rounded-lg px-1 py-0.5 md:py-1">
                        <button
                            onClick={handleZoomOut}
                            disabled={userZoom <= MIN_ZOOM}
                            className="p-1 md:p-1.5 hover:bg-white rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ZoomOut className="w-4 h-4" />
                        </button>
                        <button
                            onClick={handleResetZoom}
                            className="text-[10px] md:text-xs font-semibold text-gray-700 px-1 md:px-2 min-w-[40px] md:min-w-[50px] text-center hover:bg-white rounded-md py-0.5 md:py-1 transition-colors"
                        >
                            {displayZoomPercent}%
                        </button>
                        <button
                            onClick={handleZoomIn}
                            disabled={userZoom >= MAX_ZOOM}
                            className="p-1 md:p-1.5 hover:bg-white rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ZoomIn className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="w-px h-5 md:h-6 bg-gray-200" />

                    <button
                        onClick={handleRotate}
                        className="p-1 md:p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <RotateCw className="w-4 h-4" />
                    </button>
                </div>

                {/* PDF Content */}
                <div
                    ref={containerRef}
                    className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center"
                >
                    {error ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
                            <div className="text-red-500 font-bold text-lg">Failed to load PDF</div>
                            <p className="text-gray-500 text-sm max-w-md">{error}</p>
                            <button
                                onClick={() => { setError(null); setLoading(true); }}
                                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-black transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center min-h-full">
                            {loading && (
                                <div className="flex items-center justify-center h-64">
                                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                                </div>
                            )}
                            <Document
                                file={pdfUrl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading=""
                            >
                                <Page
                                    pageNumber={currentPage}
                                    scale={computedScale}
                                    rotate={rotation}
                                    className="shadow-xl"
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                    onLoadSuccess={onPageLoadSuccess}
                                />
                            </Document>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentPdfPreviewModal;
